import { useId, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import { IndicatorStep } from './IndicatorStep';
import { IntentTriggeredTranscriptComponent } from './types';
import { ColorMode } from 'src/components/app/types';
import { IntentPredictionMessage } from 'src/pages/workflow-builder-preview/voice-preview/inbound-preview/info-console/PreviewExecutionMessage';

const ExpandableIndicatorStep = ({
  message,
  reasoning,
}: {
  message: string;
  reasoning: string;
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const panelId = useId();

  const toggle = () => setIsExpanded(prev => !prev);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <Box display='flex' flexDirection='column' gap='6px' width='100%'>
      <Box
        aria-controls={panelId}
        aria-expanded={isExpanded}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        role='button'
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.colors.slate[100]}`,
          borderRadius: '5px',
          cursor: 'pointer',
          display: 'flex',
          gap: '6px',
          justifyContent: 'space-between',
          padding: '6px 12px',
          width: '100%',
        }}
        tabIndex={0}
      >
        <Typography
          color={theme.palette.colors.grey[600]}
          variant='font12Medium'
        >
          {message}
        </Typography>
        {isExpanded ? (
          <IconChevronDown size={16} />
        ) : (
          <IconChevronRight size={16} />
        )}
      </Box>
      <Box
        hidden={!isExpanded}
        id={panelId}
        sx={{
          backgroundColor: theme.palette.colors.slate[100],
          borderRadius: '5px',
          padding: '8px 12px',
        }}
      >
        <Typography color={theme.palette.colors.grey[700]} variant='font12'>
          {reasoning}
        </Typography>
      </Box>
    </Box>
  );
};

export const IntentTrigger = ({
  theme,
  transcriptComponent,
}: {
  theme?: ColorMode;
  transcriptComponent: IntentTriggeredTranscriptComponent;
}) => {
  const title = transcriptComponent.intent_title;
  const { reasoning } = transcriptComponent;

  if (theme === 'dark') {
    return (
      <IntentPredictionMessage
        payload={{ intent: title, type: 'intent_prediction' }}
      />
    );
  }

  const message = `Triggered workflow: ${title}`;
  return reasoning ? (
    <ExpandableIndicatorStep message={message} reasoning={reasoning} />
  ) : (
    <IndicatorStep message={message} />
  );
};
